/** Reusable admin notification stack — UI model (API-agnostic). */

export type AdminNotificationSeverity = 'info' | 'warning' | 'critical';

export type AdminNotificationStatus = 'active' | 'acknowledged' | 'resolved';

export type AdminNotificationStackItem = {
  id: string;
  severity: AdminNotificationSeverity;
  category?: string;
  /** Stable key for future grouping / dedupe (e.g. API notification_key). */
  dedupeKey?: string;
  title: string;
  message: string;
  /** Secondary line from API context/details when it adds detail beyond message. */
  subtitle?: string;
  createdAt: string;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  status?: AdminNotificationStatus;
  sopId?: number | null;
  siteId?: number | null;
  entityType?: string | null;
  entityId?: number | null;
};

export const SEVERITY_SORT: Record<AdminNotificationSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

export function isActiveNotification(item: AdminNotificationStackItem): boolean {
  return item.status !== 'resolved' && item.status !== 'acknowledged';
}

export function sortAdminNotificationStackItems(items: AdminNotificationStackItem[]): AdminNotificationStackItem[] {
  return [...items]
    .filter(isActiveNotification)
    .sort((a, b) => {
      const bySeverity = SEVERITY_SORT[a.severity] - SEVERITY_SORT[b.severity];
      if (bySeverity !== 0) return bySeverity;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
}

export function relativeAgeFromIso(createdAt: string): string {
  const timestamp = new Date(createdAt).getTime();
  if (!Number.isFinite(timestamp)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function issueCountLabel(count: number): string {
  if (count === 1) return '1 issue needs attention';
  return `${count} issues need attention`;
}

export function highestSeverity(
  items: AdminNotificationStackItem[],
): AdminNotificationSeverity | null {
  if (!items.length) return null;
  const sorted = sortAdminNotificationStackItems(items);
  return sorted[0]?.severity ?? null;
}
